import { cardCtxUrl, type ToolPage } from './cardSeo.ts'

import { effectiveCardCover, trendMemberPreview, trendMembersOf, type BackendCard } from './popularCardApply.ts'
import type { GenerationCard } from '../../../types/generationCard.ts'

/** BackendCard (карточка popular_category) → GenerationCard для CardGallery.
 *
 *  Галерейные витрины (Тренды, Фотостудия) рисуют карточки общей плиткой
 *  CardGallery, а не PopularModelCard: плитка держит натуральные пропорции
 *  превью и ховер-видео. Здесь — ТОЛЬКО поля отображения; клик берёт исходную
 *  карточку (rawById у вызывающей стороны) и идёт через applyPopularItem,
 *  корректный и для одношаговых, и для пайплайн-карточек «фото → видео».
 */
export function backendCardToGalleryCard(
	card: BackendCard,
	page: ToolPage,
	hrefCtx?: string | null,
): GenerationCard {
	// Плитка = обложка, видео — слой ПОВЕРХ неё, играет на ховер (hoverVideoUrl):
	// логика карточки одна во всех разделах, раздел задаёт только форму плитки.
	// Обложки нет — видео само становится плиткой (его первый кадр вместо неё),
	// иначе видео-карточки без баннера рендерились пустой плиткой со сломанной
	// иконкой.
	// Мультистиль: обложка = первый стиль (владелец 2026-08-16); превью-webp
	// баннера уместно только при явном баннере.
	const banner = card.background_url
		? card.background_preview_url || card.background_url
		: effectiveCardCover(card)
	const videoTile = !banner && !!card.video_url
	return {
		id: galleryCardId(card),
		type: 'style',
		previewUrl: banner || card.video_url || '',
		// Оригинал обложки для лайтбокса «глазика»: плитка живёт на лёгком
		// webp-превью, полноразмер грузится ПО ЗАПРОСУ при открытии просмотра.
		sourceImageUrl: card.background_url || undefined,
		prompt: card.system_prompt || '',
		promptLocked: card.prompt_locked,
		modelId: card.target_model || '',
		modelName: '',
		isVideo: videoTile,
		kind: videoTile ? 'video' : 'image',
		// Есть и обложка, и видео — видео едет отдельным слоем поверх обложки.
		hoverVideoUrl: banner && card.video_url ? card.video_url : undefined,
		// Плитка = само видео (обложки нет) → постером служит его первый кадр.
		videoPosterUrl: videoTile ? card.video_preview_url || undefined : undefined,
		// Лёгкий сосед оригинала — им плитка и играет (и только он включает автоплей).
		loopVideoUrl: card.video_loop_url || undefined,
		title: card.title || '',
		settings: card.settings,
		// Мультистиль: галерейная плитка несёт бейдж «N стилей» и свотчи.
		card_kind: card.card_kind,
		// Мульти-тренд: бейдж «N трендов» и свотчи участников.
		trendMembers: trendMembersOf(card).map(trendMemberPreview),
		// Карточка-флоу: лента плитки (иконка, «N шагов», «от N»), href — сам шаблон.
		flowTemplateId: card.flow_template_id,
		flowSteps: card.flow?.steps,
		flowPriceFrom: card.flow?.price_from,
		flowCoverPosition: card.flow?.cover_position ?? null,
		demoVariants: card.demo_variants || undefined,
		// Якорь лендинга /tool/{slug}: плитка получает data-card-slug.
		slug: card.slug,
		// «Стекло-капля» (админ): доп-стиль плитки в masonry-галерее.
		tileStyle: card.tile_style || undefined,
		// Плитка — ссылка на СТРАНИЧНЫЙ лендинг (/bots/<раздел>/tool/…): клик и
		// копирование не уводят с текущей витрины, даже если карточка живёт и на
		// других лейнах (canonical у лендинга — свой, primary). Контекст витрины
		// (all/popular/категория) сохраняется и в href плитки — средняя кнопка и
		// копирование ссылки не перескакивают вкладку.
		href: cardCtxUrl(card, page, hrefCtx) ?? undefined,
	}
}

/** Id плитки галереи. Свой префикс — чтобы обратный поиск исходной карточки
 *  (rawById) не пересекался с id генераций пользователя в тех же галереях. */
export function galleryCardId(card: BackendCard): string {
	return `pcard-${card.id}`
}
